import type { ComponentType } from 'react'

/**
 * `propelauth_cloud` provider — a thin passthrough to PropelAuth's React SDK.
 *
 * This is the ONLY module that imports `@propelauth/*` (the React SDK and the
 * frontend-apis login SDK), so all PropelAuth runtime resolves to one set of
 * module instances — in particular one `@propelauth/react` `AuthContext`, which
 * is why the login flow must import these through here rather than directly.
 * Builds that select another provider (e.g. `keycloak`) alias `active-provider`
 * away from here, so this file — and all PropelAuth runtime — is excluded from
 * those bundles.
 */
import { withAuthInfo as propelauthWithAuthInfo } from '@propelauth/react'
import type { AuthProviderName } from '@sweetauth/core/registry'
import type { WithAuthInfoArgs, WithAuthInfoProps } from '@sweetauth/core/types'

export {
  AuthProvider,
  RequiredAuthProvider,
  useAuthInfo,
  useHostedPageUrls,
  useLogoutFunction,
  useOrgHelper,
} from '@propelauth/react'

// The hosted-login flow's hooks + enums + response types. These read the same
// AuthContext as the SDK above, so they must come from this module too.
export {
  MfaStatus,
  useAuthFrontendApis,
  useMfa,
} from '@propelauth/frontend-apis-react'
export type { UseMfaReturnType } from '@propelauth/frontend-apis-react'
export { LoginState, SocialLoginProvider } from '@propelauth/frontend-apis'
export type {
  ApiErrorResponse,
  EmailPasswordLoginRequestBadRequestResponse,
  ForgotPasswordBadRequestResponse,
  MfaDisabledResponse,
  MfaEnabledResponse,
  SignupBadRequestResponse,
} from '@propelauth/frontend-apis'

// `@sweetauth/core`'s provider-agnostic account-management surface, implemented
// on the frontend-apis SDK above.
export {
  useMfaManagement,
  useUpdateEmail,
  useUpdatePassword,
} from './management'

/**
 * PropelAuth's `withAuthInfo` is generic over its *own* `WithAuthInfoProps`.
 * Re-type it to our self-contained types so consumers (e.g. the error boundary)
 * match regardless of provider — the keycloak provider exports the same
 * signature. The runtime is PropelAuth's, unchanged.
 */
export const withAuthInfo = propelauthWithAuthInfo as unknown as <
  P extends WithAuthInfoProps,
>(
  Component: ComponentType<P>,
  args?: WithAuthInfoArgs,
) => ComponentType<Omit<P, keyof WithAuthInfoProps>>

/**
 * BFF cookie-mode 401-retry hook for the global fetch wrapper. PropelAuth manages token refresh in
 * its own SDK and never runs in cookie mode, so this is an inert stub reporting "not refreshed" — the
 * wrapper only invokes it under the Keycloak provider. Kept here so the seam surface matches.
 */
export const refreshSessionForFetch = async (
  _authUrl: string,
): Promise<boolean> => false

/** The build-selected provider — read by apps to pick provider-specific config. */
export const providerName: AuthProviderName = 'propelauth_cloud'

/**
 * PropelAuth's hosted frontend origin, from the build-time env. Lives in the
 * provider (not shared app code) so a keycloak bundle never references the
 * NEXT_PUBLIC_PROPEL_AUTH_* identifier — knox's static content scan forbids it.
 *
 * This is a browser package (no @types/node): declare the one Node global the
 * bundler statically replaces. NEXT_PUBLIC_* references never survive to
 * runtime — Next/Turbopack inline the value at build time.
 */
declare const process: {
  env: { NEXT_PUBLIC_PROPEL_AUTH_FRONTEND_URL?: string }
}

export const authFrontendUrl: string =
  process.env.NEXT_PUBLIC_PROPEL_AUTH_FRONTEND_URL ?? ''
